import { auth } from '../auth/auth';
import { getIpqsRiskContext } from '../risk/ipqs-context';
import { QueueItem } from './queue-item.interface';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import i18n from 'src/i18n';

const backendUrl: string = import.meta.env.VITE_BACKEND_URL as string;

const axiosInstance = axios.create({
  baseURL: backendUrl, // Set the base URL for the API requests.
  withCredentials: true, // Include credentials like cookies in requests.
});

let isRefreshing = false; // Flag to track if the token refresh process is ongoing.
let failedQueue: QueueItem[] = []; // Array to hold failed requests while the token is being refreshed.

/**
 * Function to process the queued requests after the token is refreshed or an error occurs.
 * @param error - Error object if the refresh process failed.
 * @param token - New access token if the refresh process succeeded.
 */
const processQueue = (error: AxiosError | null, token: string | null = null) => {
  // Function to process the queued requests after token refresh.
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error); // Reject the promise if there was an error.
    } else {
      prom.resolve(token); // Resolve the promise with the new token.
    }
  });

  failedQueue = []; // Clear the queue after processing.
};

// Store auth state clearing function
let authStateClearFunction: (() => void) | null = null;

// Function to register the auth state clear function
export const registerAuthClearFunction = (clearFunction: () => void) => {
  authStateClearFunction = clearFunction;
};

// Add an interceptor to handle responses and retry failed requests when a 401 error occurs.
axiosInstance.interceptors.request.use((config) => {
  const { deviceId, deviceFraudChance, deviceRequestId, deviceGuid, internalDeviceId, internalDeviceConfidence } =
    getIpqsRiskContext();
  config.headers = config.headers ?? {};
  if (deviceId) {
    config.headers['x-ipqs-device-id'] = deviceId;
  }
  if (typeof deviceFraudChance === 'number') {
    config.headers['x-ipqs-device-fraud-chance'] = String(deviceFraudChance);
  }
  if (deviceRequestId) {
    config.headers['x-ipqs-device-request-id'] = deviceRequestId;
  }
  if (deviceGuid) {
    config.headers['x-ipqs-device-guid'] = deviceGuid;
  }
  if (internalDeviceId) {
    config.headers['x-device-id'] = internalDeviceId;
  }
  if (typeof internalDeviceConfidence === 'number') {
    config.headers['x-device-confidence'] = String(internalDeviceConfidence);
  }
  return config;
});

// Add an interceptor to handle responses and retry failed requests when a 401 error occurs.
axiosInstance.interceptors.response.use(
  (response) => response, // Return successful responses as is.
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Skip refresh token logic for the refresh token request itself
    if (originalRequest.url?.includes('auth/refresh-access-token')) {
      return Promise.reject(error);
    }

    // If a 401 Unauthorized error occurs and the request hasn't been retried yet.
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if the error is 401 (Unauthorized) and the request hasn't been retried yet.

      if (isRefreshing) {
        // If a token refresh is already in progress, queue the request.
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject }); // Add the current request to the queue.
        })
          .then((token) => {
            // Once the token is refreshed, retry the original request with the new token.
            originalRequest.headers['Authorization'] = 'Bearer ' + String(token);
            return axiosInstance(originalRequest); // Retry the request with the new token.
          })
          .catch((err) => Promise.reject(err)); // If retry fails, propagate the error
      }

      originalRequest._retry = true; // Mark the request as retried to avoid loops.
      isRefreshing = true; // Set the flag indicating that token refresh is in progress.

      try {
        const { accessToken } = await auth.refreshAccessToken(); // Attempt to refresh the token.
        axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken; // Update axios default headers with new token.
        originalRequest.headers['Authorization'] = 'Bearer ' + accessToken; // Update original request headers with new token.
        processQueue(null, accessToken); // Process the queued requests with the new token.
        return axiosInstance(originalRequest); // Retry the original request.
      } catch (err) {
        processQueue(err as AxiosError, null); // If refresh fails, process the queue with the error
        isRefreshing = false;
        // If refreshToken fails, we need to propagate this error
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          // Instead of using the hook, use the registered clear function
          if (authStateClearFunction) {
            authStateClearFunction(); // Clear authentication state using the registered function
          }

          // Let the routing system handle the redirect instead of doing it here
          throw new Error(i18n.t('ERRORS.REFRESH_TOKEN_FAILED')); // Throw a specific error.
        }
        return Promise.reject(err); // Propagate other errors.
      } finally {
        isRefreshing = false; // Reset the refreshing flag.
      }
    }

    return Promise.reject(error); // Reject any other errors not related to 401.
  }
);

export default axiosInstance; // Export the axios instance with the interceptor configured.
